/**
 * SecureEmbed Quick Actions Component
 *
 * Reusable deployment quick actions: Deploy/Destroy, Power Management, Resource Controls, AWS Console
 * Extracted from DeploymentHub for use on Monitor page
 */

import React, { useState, useEffect } from 'react';
import {
  Play, XCircle, Loader2, ArrowRight, Flame,
  Moon, Sun, Eye, ExternalLink, Zap,
} from 'lucide-react';
import { operationsApi, powerApi, ENV_COLORS } from '../services/api';

interface QuickActionsProps {
  environment: string;
}

const QuickActions: React.FC<QuickActionsProps> = ({ environment }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [isDestroying, setIsDestroying] = useState(false);
  const [sleepState, setSleepState] = useState<string>('unknown');
  const [powerState, setPowerState] = useState<any>(null);

  const envColors = ENV_COLORS[environment as keyof typeof ENV_COLORS] || ENV_COLORS.dev;
  const isAdmin = true; // Simplified — real auth check done at layout level

  // Fetch power state
  useEffect(() => {
    const fetchPower = async () => {
      try {
        const data = await powerApi.getState({ env: environment });
        setPowerState(data);
        setSleepState(data?.is_running === false ? 'sleeping' : data?.is_running === true ? 'awake' : 'unknown');
      } catch {
        // Silent fail
      }
    };
    fetchPower();
    const interval = setInterval(fetchPower, 30000);
    return () => clearInterval(interval);
  }, [environment]);

  const startDeployment = async () => {
    setIsRunning(true);
    setIsDestroying(false);
    try {
      await operationsApi.deploy({ env: environment, layer: 'all' });
    } catch (err) {
      console.error('Deploy error:', err);
    }
  };

  const startDestroy = async () => {
    setIsRunning(true);
    setIsDestroying(true);
    try {
      await operationsApi.destroy({ env: environment, layer: 'all' });
    } catch (err) {
      console.error('Destroy error:', err);
    }
  };

  const stopDeployment = async () => {
    try {
      await operationsApi.stop({ env: environment });
      setIsRunning(false);
    } catch (err) {
      console.error('Stop error:', err);
    }
  };

  const sleepEnvironment = async () => {
    setSleepState('sleeping_transition');
    try {
      await powerApi.setPower({ env: environment, action: 'stop' });
      setSleepState('sleeping');
    } catch {
      setSleepState('awake');
    }
  };

  const wakeEnvironment = async () => {
    setSleepState('waking');
    try {
      await powerApi.setPower({ env: environment, action: 'start' });
      setSleepState('awake');
    } catch {
      setSleepState('sleeping');
    }
  };

  return (
    <div className="rounded-xl border-2 shadow-sm overflow-hidden" style={{ backgroundColor: envColors.bg, borderColor: envColors.primary }}>
      <div className="p-4 border-b border-gray-100">
        <h3 className="font-semibold flex items-center gap-2 text-gray-900">
          <Zap className="w-5 h-5 text-yellow-500" />
          Quick Actions
        </h3>
      </div>
      <div className="p-4 space-y-3">
        {isAdmin && (isRunning ? (
          <button
            onClick={stopDeployment}
            className={`w-full flex items-center gap-3 p-3 rounded-lg font-medium transition-all border
                     ${isDestroying
                       ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100 animate-pulse'
                       : 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100'}`}
          >
            <XCircle className="w-4 h-4" />
            <span>Stop {isDestroying ? 'Destroy' : 'Deploy'}</span>
            <Loader2 className="w-4 h-4 ml-auto animate-spin" />
          </button>
        ) : (
          <>
            <button
              onClick={startDeployment}
              className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50
                       hover:from-green-100 hover:to-emerald-100 text-green-600 rounded-lg font-medium
                       transition-all border border-green-200"
            >
              <Play className="w-4 h-4" />
              <span>Deploy Environment</span>
              <ArrowRight className="w-4 h-4 ml-auto" />
            </button>
            <button
              onClick={startDestroy}
              className="w-full flex items-center gap-3 p-3 bg-red-50 hover:bg-red-100
                       text-red-600 rounded-lg font-medium transition-all border border-red-200"
            >
              <Flame className="w-4 h-4" />
              <span>Destroy Environment</span>
              <ArrowRight className="w-4 h-4 ml-auto" />
            </button>
          </>
        ))}

        {/* Sleep/Wake */}
        <div className="pt-2 border-t border-gray-100 space-y-2">
          {environment !== 'prod' ? (
            sleepState === 'sleeping' ? (
              <button
                onClick={wakeEnvironment}
                className="w-full flex items-center gap-3 p-3 bg-yellow-50 hover:bg-yellow-100
                         text-yellow-700 rounded-lg font-medium transition-all border border-yellow-200"
              >
                <Sun className="w-4 h-4" />
                <span>Wake Environment</span>
              </button>
            ) : sleepState === 'awake' ? (
              <button
                onClick={sleepEnvironment}
                className="w-full flex items-center gap-3 p-3 bg-gray-100 hover:bg-gray-200
                         text-gray-700 rounded-lg font-medium transition-all border border-gray-200"
              >
                <Moon className="w-4 h-4" />
                <span>Sleep Environment</span>
              </button>
            ) : (
              <button
                className="w-full flex items-center gap-3 p-3 bg-gray-50 text-gray-500 rounded-lg font-medium border border-gray-200"
                disabled
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{sleepState === 'waking' ? 'Waking...' : 'Checking...'}</span>
              </button>
            )
          ) : (
            <button
              className="w-full flex items-center gap-3 p-3 bg-gray-50 text-gray-500 rounded-lg font-medium border border-gray-200"
              disabled
            >
              <Moon className="w-4 h-4" />
              <span>Sleep Controls (PROD disabled)</span>
            </button>
          )}
        </div>

        {/* Power Status */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
          <div className="font-semibold text-gray-800 mb-2">Power Status</div>
          <div className="flex items-center justify-between">
            <span>EKS</span>
            <span className="font-medium">{powerState?.eks === 'not_found' ? 'Sleeping' : powerState?.eks ?? 'Unknown'}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span>Aurora</span>
            <span className="font-medium">{powerState?.aurora === 'not_found' ? 'Sleeping' : powerState?.aurora ?? 'Unknown'}</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="font-semibold">Overall</span>
            <span className="font-semibold">{powerState?.is_running === false ? 'sleeping' : powerState?.is_running === true ? 'awake' : 'Unknown'}</span>
          </div>
        </div>

        {/* AWS Console Link */}
        <a
          href="https://ap-southeast-2.console.aws.amazon.com/codesuite/codebuild/projects"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100
                   text-blue-600 rounded-lg transition-all"
        >
          <Eye className="w-4 h-4" />
          <span>AWS Console</span>
          <ExternalLink className="w-4 h-4 ml-auto" />
        </a>
      </div>
    </div>
  );
};

export default QuickActions;
